/**
 * Abschnitte einer Mail: ganze Absätze, Kästen und Zeilen, die man samt
 * Hintergrund, Rand und Abstand aus der Mail nehmen kann (Anzeige, DOM).
 * Gegenstück im gespeicherten HTML: anwenden.ts (entfernenAnwenden).
 *
 * Von einer Stelle in der Mail aus führt eine Kette nach außen: Absatz →
 * Kasten → Zeile … Aufeinanderfolgende Elemente mit demselben Inhalt (eine
 * Tabellenzelle, die nur diesen einen Absatz enthält) zählen als eine Stufe,
 * und zwar als das äußerste von ihnen — so bleibt beim Entfernen keine leere
 * Hülle stehen. Nicht angeboten wird, was die Mail leer räumt oder den
 * Abmelde-Link enthält.
 */
import { textbloecke, elementBei, elementSchluessel, istUi, normalisieren, type Aenderung, type BlockSchluessel } from './bloecke';
import { domBlockText, OHNE_TEXT } from './quelltext';
import {
  ABMELDE, ABSCHNITT_BILDER_MAX, ABSCHNITT_BILD_MAX, ABSCHNITT_TAGS, ABSCHNITT_TEXT_MAX, VERSTECKT_STIL, abschnittArt,
} from './regeln';

export interface Abschnitt {
  el: HTMLElement;
  schluessel: BlockSchluessel;
  /** Absatz, Kasten, Zeile … */
  art: string;
  /** Ganzer Text (normalisiert) */
  text: string;
  bilder: string[];
  /** Zahl der Textstellen darin */
  textstellen: number;
  /** Anfang des Textes für die Anzeige */
  vorschau: string;
}

/** Was sich nicht ändert, solange die Mail geladen ist: die sichtbaren Textstellen. */
export interface AbschnittKontext { sichtbar: HTMLElement[] }

/** Warum nichts angeboten wird. */
export type AbschnittGrund = 'leer' | 'abmelden' | 'alles' | 'zu_gross' | 'nicht_sauber';

function versteckt(el: Element): boolean {
  for (let e: Element | null = el; e; e = e.parentElement) {
    const s = e.getAttribute('style');
    if (s && VERSTECKT_STIL.test(s)) return true;
  }
  return false;
}

export function abschnittKontext(doc: Document): AbschnittKontext {
  return { sichtbar: textbloecke(doc).filter((b) => !versteckt(b.el)).map((b) => b.el) };
}

/** Bilder (src) unter `el` in Dokumentreihenfolge — Bilder in Skripten, Vorlagen u. Ä. zählen nicht. */
export function abschnittBilder(el: Element): string[] {
  const out: string[] = [];
  const laufen = (e: Element) => {
    for (let k = e.firstElementChild; k; k = k.nextElementSibling) {
      if (OHNE_TEXT.has(k.localName)) continue;
      if (k.localName === 'img') {
        const s = k.getAttribute('src');
        if (s) out.push(s);
      }
      laufen(k);
    }
  };
  laufen(el);
  return out;
}

function inhaltsSchluessel(text: string, bilder: string[]): string {
  return `${text}\u0001${bilder.join('\u0002')}`;
}

function kurz(text: string, max = 60): string {
  const t = normalisieren(text);
  return t.length > max ? `${t.slice(0, max - 1).trimEnd()}…` : t;
}

/** Zahl der Textstellen in `el` (steht der Text direkt darin, ist es eine). */
function zaehlen(el: Element, text: string): number {
  return Math.max(textbloecke(el).length, text ? 1 : 0);
}

/**
 * Die Stufen von `ziel` nach außen (kleinste zuerst). Leer, wenn an dieser
 * Stelle nichts zum Entfernen steht; `grund` sagt dann, warum.
 * `passt` darf Stufen aussortieren (z. B. weil sie sich im Quelltext nicht
 * sauber herausnehmen lassen).
 */
export function abschnittsKette(
  doc: Document, ziel: Node | null, ctx: AbschnittKontext, passt?: (a: Abschnitt) => boolean,
): { kette: Abschnitt[]; grund: AbschnittGrund | null } {
  const start = !ziel ? null : ziel.nodeType === 1 ? (ziel as Element) : ziel.parentElement;
  const huellen: HTMLElement[] = [];
  for (let e: Element | null = start; e && e !== doc.body && e !== doc.documentElement; e = e.parentElement) {
    if (ABSCHNITT_TAGS.has(e.localName) && !istUi(e) && !OHNE_TEXT.has(e.localName)) huellen.push(e as HTMLElement);
  }

  // Gleicher Inhalt = eine Stufe, und zwar die äußerste
  const stufen: { el: HTMLElement; text: string; bilder: string[]; key: string }[] = [];
  for (const el of huellen) {
    const text = domBlockText(el);
    const bilder = abschnittBilder(el);
    if (!text && !bilder.length) continue;
    const key = inhaltsSchluessel(text, bilder);
    if (stufen.length && stufen[stufen.length - 1].key === key) stufen[stufen.length - 1] = { el, text, bilder, key };
    else stufen.push({ el, text, bilder, key });
  }
  // Ein einzelnes Zeichen (Haken, Punkt) ist keine sinnvolle Stufe, wenn es größere gibt
  const sinnvoll = stufen.filter((s) => s.text.length >= 3 || s.bilder.length > 0);
  const wahl = sinnvoll.length ? sinnvoll : stufen;
  if (!wahl.length) return { kette: [], grund: 'leer' };

  const kette: Abschnitt[] = [];
  let grund: AbschnittGrund | null = null;
  let aussortiert = false;
  for (const s of wahl) {
    if (ABMELDE.test(s.el.outerHTML)) { grund = 'abmelden'; break; }
    if (!ctx.sichtbar.some((b) => !s.el.contains(b))) { grund = 'alles'; break; }
    if (s.text.length > ABSCHNITT_TEXT_MAX || s.bilder.length > ABSCHNITT_BILDER_MAX
      || s.bilder.some((b) => b.length > ABSCHNITT_BILD_MAX)) { grund = 'zu_gross'; break; }
    const schluessel = elementSchluessel(doc, s.el);
    if (!schluessel) continue;
    const a: Abschnitt = {
      el: s.el, schluessel, art: abschnittArt(s.el.localName), text: s.text, bilder: s.bilder,
      textstellen: zaehlen(s.el, s.text), vorschau: kurz(s.text),
    };
    if (passt && !passt(a)) { aussortiert = true; continue; }
    kette.push(a);
  }
  return { kette, grund: kette.length ? null : (grund ?? (aussortiert ? 'nicht_sauber' : 'leer')) };
}

/** Die Änderung, die aus einem Abschnitt wird (gespeichert im Vorschlag). */
export function abschnittAenderung(a: Abschnitt): Aenderung {
  return {
    block: { tag: a.schluessel.tag, nr: a.schluessel.nr },
    vorher_text: a.text,
    nachher_text: '',
    vorher_knoten: [],
    nachher_knoten: [],
    nachher_html: null,
    entfernen: { art: a.art, bilder: [...a.bilder] },
  };
}

function gleicheBilder(a: string[], b: string[]): boolean {
  return a.length === b.length && a.every((x, i) => x === b[i]);
}

/**
 * Der Abschnitt zu einer Entfernung in der Anzeige: an seiner Stelle, sonst
 * das einzige Element dieses Tags mit demselben Inhalt.
 */
export function abschnittFinden(doc: Document, a: Aenderung): HTMLElement | null {
  const e = a.entfernen;
  if (!e || !a.block) return null;
  const text = normalisieren(a.vorher_text);
  const passt = (el: Element) => !istUi(el) && domBlockText(el) === text && gleicheBilder(abschnittBilder(el), e.bilder);
  const genau = elementBei(doc, a.block);
  if (genau && passt(genau)) return genau as HTMLElement;
  const treffer = Array.from(doc.getElementsByTagName(a.block.tag)).filter(passt);
  return treffer.length === 1 ? (treffer[0] as HTMLElement) : null;
}

/** „Kasten mit 3 Textstellen und 1 Bild" — für Knöpfe und Hinweise. */
export function abschnittBeschreibung(a: Pick<Abschnitt, 'art' | 'textstellen' | 'bilder'>): string {
  const teile: string[] = [];
  if (a.textstellen > 1) teile.push(`${a.textstellen} Textstellen`);
  if (a.bilder.length) teile.push(a.bilder.length === 1 ? '1 Bild' : `${a.bilder.length} Bilder`);
  return teile.length ? `${a.art} mit ${teile.join(' und ')}` : a.art;
}
